/**
 * @file StatsigWebProvider.ts
 * @description Thin OpenFeature web Provider backed by the Statsig JS client.
 *
 * No turnkey Statsig OpenFeature *web* provider exists on npm (only a .NET one),
 * so this is the small glue that lets every flag read in the app go through the
 * OpenFeature API — which is the whole reason we standardized on OpenFeature:
 * the provider stays swappable. Replacing Statsig later is swapping this one file.
 *
 * Mapping: boolean flags -> Statsig feature gates (`checkGate`); string/number/
 * object flags -> the `value` field of a Statsig dynamic config.
 *
 * The web Provider resolves synchronously (static-context model): Statsig is
 * fetched once in `initialize`, then `resolve*` read the cached values. Live
 * flips arrive via Statsig's `values_updated` event, re-emitted as an
 * OpenFeature configuration-changed event so subscribed components re-render.
 */
import {
  type EvaluationContext,
  type JsonValue,
  type Provider,
  type ResolutionDetails,
  OpenFeatureEventEmitter,
  ProviderEvents,
  StandardResolutionReasons,
} from '@openfeature/web-sdk'
import { StatsigClient, type StatsigUser } from '@statsig/js-client'

/** Statsig environment tier. Per-environment keys + tier hard-isolate envs. */
export type RolloutTier = 'development' | 'staging' | 'production'

const ANONYMOUS_ID_KEY = 'halo:rollout-anon-id'

/**
 * A stable, purely-local anonymous identity. Statsig gates configured with
 * "userID" as their rollout ID Type warn — "The user does not
 * have the required id_type" — whenever `StatsigUser.userID` is empty, which
 * is exactly the pre-auth window before `setRolloutContext` has a real org/
 * subject (see context.ts's own "falls back to an empty key (global)" case).
 * This gives every evaluation a non-empty userID regardless of auth state,
 * without touching the OpenFeature `targetingKey`/`orgAccountId` other gates'
 * rule conditions key on. Not sent anywhere but Statsig; not PII.
 */
export function getAnonymousId(): string {
  try {
    const existing = window.localStorage.getItem(ANONYMOUS_ID_KEY)
    if (existing) return existing
    const id = crypto.randomUUID()
    window.localStorage.setItem(ANONYMOUS_ID_KEY, id)
    return id
  } catch {
    // localStorage unavailable (privacy mode, SSR, etc.) — a per-call random id
    // still satisfies the id_type check; it just won't be stable across reloads.
    return crypto.randomUUID()
  }
}

export class StatsigWebProvider implements Provider {
  readonly metadata = { name: 'statsig-web' } as const
  readonly runsOn = 'client' as const
  readonly events = new OpenFeatureEventEmitter()

  private client: StatsigClient | undefined
  private readonly sdkKey: string
  private readonly tier: RolloutTier

  constructor(sdkKey: string, tier: RolloutTier) {
    this.sdkKey = sdkKey
    this.tier = tier
  }

  /**
   * Statsig user from the OpenFeature context. Opaque org id only — never PII.
   * `userID` always resolves to a non-empty value (falling back to a local
   * anonymous id pre-auth) — see getAnonymousId() above for why.
   */
  private toUser(context?: EvaluationContext): StatsigUser {
    const targetingKey =
      typeof context?.targetingKey === 'string' ? context.targetingKey : ''
    const userID = targetingKey.length > 0 ? targetingKey : getAnonymousId()
    const orgAccountId =
      typeof context?.orgAccountId === 'string' ? context.orgAccountId : undefined
    return { userID, custom: orgAccountId ? { orgAccountId } : undefined }
  }

  async initialize(context?: EvaluationContext): Promise<void> {
    const client = new StatsigClient(this.sdkKey, this.toUser(context), {
      environment: { tier: this.tier },
    })
    await client.initializeAsync()
    client.on('values_updated', () => {
      this.events.emit(ProviderEvents.ConfigurationChanged)
    })
    this.client = client
  }

  async onContextChange(
    _oldContext: EvaluationContext,
    newContext: EvaluationContext,
  ): Promise<void> {
    await this.client?.updateUserAsync(this.toUser(newContext))
  }

  async onClose(): Promise<void> {
    await this.client?.shutdown()
  }

  resolveBooleanEvaluation(
    flagKey: string,
    defaultValue: boolean,
  ): ResolutionDetails<boolean> {
    if (!this.client) {
      return { value: defaultValue, reason: StandardResolutionReasons.ERROR }
    }
    return {
      value: this.client.checkGate(flagKey),
      reason: StandardResolutionReasons.TARGETING_MATCH,
    }
  }

  resolveStringEvaluation(
    flagKey: string,
    defaultValue: string,
  ): ResolutionDetails<string> {
    return this.fromConfig(flagKey, defaultValue)
  }

  resolveNumberEvaluation(
    flagKey: string,
    defaultValue: number,
  ): ResolutionDetails<number> {
    return this.fromConfig(flagKey, defaultValue)
  }

  resolveObjectEvaluation<T extends JsonValue>(
    flagKey: string,
    defaultValue: T,
  ): ResolutionDetails<T> {
    return this.fromConfig(flagKey, defaultValue)
  }

  /** Non-boolean flags read the `value` field of a Statsig dynamic config. */
  private fromConfig<T>(flagKey: string, defaultValue: T): ResolutionDetails<T> {
    if (!this.client) {
      return { value: defaultValue, reason: StandardResolutionReasons.ERROR }
    }
    // Statsig's get() returns a TypedReturn<T> wrapper; the runtime value is T.
    const value = this.client.getDynamicConfig(flagKey).get('value', defaultValue) as T
    return { value, reason: StandardResolutionReasons.TARGETING_MATCH }
  }
}
